import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { validateRosterProviderEmail } from '../validation';

/**
 * @typedef {import('../types').RosterProvider} RosterProvider
 */

/**
 * Provider email input that validates the address as the user types.
 * It shows a loading indicator, a success checkmark, or an error message.
 * Validation only begins after the user has started typing.
 *
 * @param {object} props
 * @param {string} props.value The current email value.
 * @param {(value: string) => void} props.onValueChange Callback to update the email value.
 * @param {(isValid: boolean) => void} props.onValidityChange Callback when email validity changes
 * @param {string} [props.placeholder] Optional placeholder text.
 * @param {string} [props.className] Optional additional CSS classes.
 * @param {boolean} [props.disabled] Whether the input is disabled.
 * @param {Array<RosterProvider>} [props.otherProviders=[]] Other providers in roster (excluding current) for duplicate checking.
 */
const EmailInput = ({
  value,
  onValueChange,
  onValidityChange,
  placeholder,
  className = '',
  disabled = false,
  otherProviders = [],
}) => {
  const [touched, setTouched] = useState(false);

  // Validate email using the pure validation function
  const { isValid, error } = useMemo(
    () => validateRosterProviderEmail(value, touched ? otherProviders : []),
    [value, otherProviders, touched],
  );

  // Notify parent when validity changes
  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  const hasError = !!error;

  return (
    <div className="flex flex-col w-full">
      <div className="relative">
        <input
          name="email"
          type="email"
          placeholder={placeholder || "Enter provider's email"}
          autoComplete="email"
          className={['w-full h-12 pr-10', className].join(' ')}
          value={value || ''}
          aria-invalid={hasError}
          onChange={(e) => {
            if (!touched) {
              setTouched(true);
            }
            onValueChange(e.target.value);
          }}
          disabled={disabled}
        />
      </div>
      {hasError && (
        <div className="pt-1 text-xs text-coral-96" role="alert">
          {error.message}
        </div>
      )}
    </div>
  );
};

EmailInput.propTypes = {
  value: PropTypes.string,
  onValueChange: PropTypes.func.isRequired,
  onValidityChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  otherProviders: PropTypes.arrayOf(PropTypes.object),
};

export default EmailInput;
